import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { Reveal, SectionLabel } from "@/components/motion";
import { PRODUCTS } from "@/data/content";

const css = `
  @keyframes bar-grow {
    from { transform: scaleY(0); }
    to   { transform: scaleY(1); }
  }
  @keyframes progress-fill {
    from { width: 0%; }
    to   { width: var(--fill); }
  }
  @keyframes task-check {
    0%,80%  { opacity: 0; transform: scale(0.5); }
    90%,100%{ opacity: 1; transform: scale(1); }
  }
  @keyframes pulse-dot {
    0%,100% { opacity: 1; transform: scale(1); }
    50%     { opacity: 0.4; transform: scale(0.7); }
  }
  @keyframes slide-row {
    0%,100% { transform: translateY(0); }
    50%     { transform: translateY(-6px); }
  }
`;

/* ── LMS Mock ────────────────────────────────────────────── */
const LMSMock = ({ accent }) => {
  const students = [
    { name: "Aisha Malik",   pct: 92, color: "#09B2ED" },
    { name: "Ryan Okafor",   pct: 74, color: "#2563EB" },
    { name: "Sara Chen",     pct: 87, color: "#09B2ED" },
    { name: "Lucas Mohr",    pct: 58, color: "#91E0F1" },
  ];
  const bars = [40, 65, 50, 85, 60, 100, 75, 55, 90, 70];

  return (
    <div className="rounded-3xl bg-white shadow-2xl border border-slate-100 overflow-hidden">
      {/* browser chrome */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 bg-[#F8FAFC]">
        <span className="w-2.5 h-2.5 rounded-full bg-rose-400/70" />
        <span className="w-2.5 h-2.5 rounded-full bg-amber-400/70" />
        <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" />
        <div className="ml-3 flex-1 max-w-[220px] h-5 rounded-md bg-white border border-slate-200 flex items-center px-2">
          <span className="font-mono text-[8px] text-slate-400">app.lms.theaurgelabs.com</span>
        </div>
      </div>

      <div className="p-5 space-y-4">
        {/* header row */}
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-bold text-[#01193D]">Course Dashboard</div>
            <div className="text-[9px] text-slate-400 font-mono">Web Development Bootcamp · 247 students</div>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full animate-pulse" style={{ background: accent }} />
            <span className="text-[9px] font-mono text-emerald-600 font-semibold">Live class active</span>
          </div>
        </div>

        {/* progress cards */}
        <div className="grid grid-cols-3 gap-2.5">
          {[
            { label: "Completion", value: "76%", icon: "🎓" },
            { label: "Avg. Score", value: "84.2",icon: "📊" },
            { label: "Certified",  value: "38",  icon: "🏅" },
          ].map((m) => (
            <div key={m.label} className="bg-[#F8FAFC] rounded-xl p-2.5 border border-slate-100">
              <div className="text-lg">{m.icon}</div>
              <div className="text-sm font-bold text-[#01193D] mt-1">{m.value}</div>
              <div className="text-[8px] text-slate-400">{m.label}</div>
            </div>
          ))}
        </div>

        {/* student progress list */}
        <div className="space-y-2">
          <div className="text-[9px] font-semibold text-slate-500 uppercase tracking-wider">Top Students</div>
          {students.map((s, i) => (
            <div key={s.name} className="flex items-center gap-2.5">
              <div className="w-5 h-5 rounded-full flex items-center justify-center text-white text-[8px] font-bold shrink-0"
                style={{ background: s.color }}>
                {s.name[0]}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[9px] text-slate-600 truncate">{s.name}</div>
                <div className="h-1.5 bg-slate-100 rounded-full mt-0.5 overflow-hidden">
                  <div
                    className="h-full rounded-full"
                    style={{
                      width: `${s.pct}%`,
                      background: s.color,
                      animation: `progress-fill 1.2s ease-out ${i * 0.15}s both`,
                      "--fill": `${s.pct}%`,
                    }}
                  />
                </div>
              </div>
              <span className="text-[8.5px] font-mono font-bold text-slate-500">{s.pct}%</span>
            </div>
          ))}
        </div>

        {/* mini sparkline */}
        <div className="pt-2 border-t border-slate-100">
          <div className="flex items-center justify-between text-[8px] text-slate-400 font-mono mb-2">
            <span>Weekly Engagement Hours</span>
            <span className="text-emerald-600 font-semibold">+18.4% vs last week</span>
          </div>
          <div className="flex items-end gap-1.5 h-10">
            {bars.map((h, i) => (
              <div
                key={i}
                className="flex-1 rounded-sm origin-bottom"
                style={{
                  height: `${h}%`,
                  background: i === 5 ? accent : "#E2E8F0",
                  animation: `bar-grow 0.6s ease-out ${i * 0.05}s both`,
                }}
              />
            ))}
          </div>
          <div className="flex justify-between text-[7px] text-slate-400 font-mono mt-1">
            <span>Mon</span><span>Wed</span><span>Fri</span><span>Sun</span>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── CRM Mock ────────────────────────────────────────── */
const CRMMock = ({ accent }) => {
  const pipeline = [
    { stage: "Leads",     count: 14, color: "#CBD5E1" },
    { stage: "Proposal",  count: 7,  color: "#93C5FD" },
    { stage: "Negotiation",count: 4, color: accent },
    { stage: "Closed ✓", count: 9,  color: "#34D399" },
  ];
  const tasks = [
    { label: "Send Acme proposal",       done: true  },
    { label: "Review TechCorp contract", done: true  },
    { label: "Follow up — BrightEdge",   done: false },
    { label: "Invoice — GlobalSync",     done: false },
  ];

  return (
    <div className="rounded-3xl bg-white shadow-2xl border border-slate-100 overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 bg-[#F8FAFC]">
        <span className="w-2.5 h-2.5 rounded-full bg-rose-400/70" />
        <span className="w-2.5 h-2.5 rounded-full bg-amber-400/70" />
        <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" />
        <div className="ml-3 flex-1 max-w-[220px] h-5 rounded-md bg-white border border-slate-200 flex items-center px-2">
          <span className="font-mono text-[8px] text-slate-400">app.crm.theaurgelabs.com</span>
        </div>
      </div>

      <div className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-bold text-[#01193D]">Sales Pipeline</div>
            <div className="text-[9px] text-slate-400 font-mono">Q3 2026 · $485,000 in pipeline</div>
          </div>
          <div className="bg-emerald-50 text-emerald-600 text-[8px] font-bold px-2.5 py-1 rounded-full border border-emerald-200">
            +22% MoM
          </div>
        </div>

        {/* Kanban pipeline */}
        <div className="grid grid-cols-4 gap-1.5">
          {pipeline.map((p, i) => (
            <div key={p.stage} className="text-center space-y-1.5">
              <div className="text-[7.5px] font-semibold text-slate-500 truncate">{p.stage}</div>
              <div className="text-lg font-bold" style={{ color: p.color === "#CBD5E1" ? "#94a3b8" : p.color }}>
                {p.count}
              </div>
              <div className="h-16 rounded-lg border border-slate-100 bg-slate-50 flex flex-col gap-1 p-1 overflow-hidden">
                {[...Array(Math.min(p.count, 3))].map((_, j) => (
                  <div key={j} className="h-2.5 rounded" style={{ background: `${p.color}40`, borderLeft: `2px solid ${p.color}` }} />
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* invoice stats */}
        <div className="grid grid-cols-2 gap-2.5">
          {[
            { label: "Outstanding", value: "$18,200", icon: "⏳", bg: "bg-amber-50", border: "border-amber-100", text: "text-amber-700" },
            { label: "Collected",   value: "$54,600", icon: "✅", bg: "bg-emerald-50", border: "border-emerald-100", text: "text-emerald-700" },
          ].map(m => (
            <div key={m.label} className={`${m.bg} ${m.border} border rounded-xl p-2.5`}>
              <div className="text-base">{m.icon}</div>
              <div className={`text-sm font-bold ${m.text} mt-0.5`}>{m.value}</div>
              <div className="text-[8px] text-slate-400">{m.label}</div>
            </div>
          ))}
        </div>

        {/* task list */}
        <div className="space-y-1.5">
          <div className="text-[9px] font-semibold text-slate-500 uppercase tracking-wider">Today's Deals</div>
          {tasks.map((t, i) => (
            <div key={t.label} className="flex items-center gap-2 text-[8.5px] text-slate-600 bg-slate-50 rounded-lg px-2.5 py-1.5">
              <span className={`w-3.5 h-3.5 rounded-full flex items-center justify-center text-[7px] text-white font-bold shrink-0 ${t.done ? "bg-emerald-500" : "border-2 border-slate-300"}`}>
                {t.done ? "✓" : ""}
              </span>
              <span className={t.done ? "line-through text-slate-400" : ""}>{t.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ── School Management System Mock ────────────────────────────────── */
const SchoolMock = ({ accent }) => {
  const classes = [
    { time: "09:00", subject: "Grade 10 Physics", teacher: "Dr. Vance", room: "Lab 2" },
    { time: "11:00", subject: "Grade 11 Mathematics", teacher: "Mrs. Davis", room: "Room 401" },
    { time: "13:30", subject: "Grade 12 CS & Robotics", teacher: "Mr. Park", room: "Comp Lab" },
  ];

  return (
    <div className="rounded-3xl bg-white shadow-2xl border border-slate-100 overflow-hidden">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 bg-[#F8FAFC]">
        <span className="w-2.5 h-2.5 rounded-full bg-rose-400/70" />
        <span className="w-2.5 h-2.5 rounded-full bg-amber-400/70" />
        <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" />
        <div className="ml-3 flex-1 max-w-[220px] h-5 rounded-md bg-white border border-slate-200 flex items-center px-2">
          <span className="font-mono text-[8px] text-slate-400">app.school.theaurgelabs.com</span>
        </div>
      </div>

      <div className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-bold text-[#01193D]">Campus Operating System</div>
            <div className="text-[9px] text-slate-400 font-mono">Oakridge Academy · 1,240 students</div>
          </div>
          <span className="font-mono text-[8px] bg-emerald-50 text-emerald-600 font-bold px-2 py-0.5 rounded-full border border-emerald-200">
            RFID Gate Active
          </span>
        </div>

        {/* 3 KPI cards */}
        <div className="grid grid-cols-3 gap-2">
          {[
            { label: "Attendance", value: "98.2%", sub: "Today", color: "#2563EB" },
            { label: "Fee Collected", value: "96.4%", sub: "Term 2", color: accent },
            { label: "Faculty On Duty", value: "84/84", sub: "All Classes", color: "#2563EB" },
          ].map(k => (
            <div key={k.label} className="bg-[#F8FAFC] rounded-xl p-2.5 border border-slate-100">
              <div className="text-sm font-bold text-[#01193D]">{k.value}</div>
              <div className="text-[7.5px] text-slate-400 font-mono mt-0.5">{k.label}</div>
              <div className="text-[7px] text-emerald-600 font-semibold">{k.sub}</div>
            </div>
          ))}
        </div>

        {/* Schedule grid */}
        <div className="space-y-1.5">
          <div className="text-[9px] font-semibold text-slate-500 uppercase tracking-wider">Today's Class Schedule</div>
          {classes.map((c, i) => (
            <div
              key={c.subject}
              className="flex items-center gap-2 p-2 rounded-xl bg-slate-50 border border-slate-100"
              style={{ animation: `slide-row 4s ease-in-out ${i * 0.8}s infinite` }}
            >
              <div className="text-[8px] font-mono font-bold text-slate-400 shrink-0 w-8">{c.time}</div>
              <div className="w-1 h-6 rounded-full shrink-0" style={{ background: accent }} />
              <div className="flex-1 min-w-0">
                <div className="text-[9px] font-bold text-[#01193D] truncate">{c.subject}</div>
                <div className="text-[7.5px] text-slate-400">{c.teacher} · {c.room}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const MOCKS = { lms: LMSMock, crm: CRMMock, school: SchoolMock };
const DISPLAY_NAMES = {
  lms: "The Aurge Labs - LMS",
  crm: "The Aurge Labs - CRM",
  school: "The Aurge Labs - School Management System"
};

export default function ProductsShowcase() {
  return (
    <section className="px-5 sm:px-8 py-20 lg:py-28 bg-[#F8FAFC]" data-testid="products-showcase-section">
      <style>{css}</style>
      <div className="max-w-7xl mx-auto">
        <Reveal className="max-w-2xl">
          <SectionLabel>Our Products</SectionLabel>
          <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-[44px] tracking-tight text-[#01193D] leading-tight">
            Real products, shipped and running
          </h2>
          <p className="text-[#475569] text-base lg:text-lg mt-4">
            We don't just build for clients — we build for markets. These are our own platforms, live and growing.
          </p>
        </Reveal>

        <div className="space-y-24 lg:space-y-32 mt-16">
          {PRODUCTS.map((p, idx) => {
            const fromLeft = idx % 2 === 0;
            const Mock = MOCKS[p.id];
            return (
              <div key={p.id} className="grid lg:grid-cols-2 gap-10 lg:gap-20 items-center" data-testid={`showcase-row-${p.id}`}>

                {/* ── Mock graphic ── */}
                <motion.div
                  initial={{ opacity: 0, x: fromLeft ? -60 : 60 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.7, ease: [0.21, 0.6, 0.35, 1] }}
                  className={fromLeft ? "" : "lg:order-2"}
                >
                  {Mock && <Mock accent={p.accent} />}
                </motion.div>

                {/* ── Text block ── */}
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.6, delay: 0.15, ease: [0.21, 0.6, 0.35, 1] }}
                  className={fromLeft ? "" : "lg:order-1"}
                >
                  {/* product identity */}
                  <div className="flex items-center gap-3 mb-5">
                    <span className="inline-flex w-12 h-12 rounded-2xl items-center justify-center shadow-lg"
                      style={{ background: `linear-gradient(135deg, ${p.accent}, #2563EB)` }}>
                      <p.icon size={22} className="text-white" />
                    </span>
                    <div>
                      <h3 className="font-display font-bold text-2xl lg:text-3xl text-[#01193D]">{DISPLAY_NAMES[p.id]}</h3>
                      <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mt-0.5">{p.tag}</p>
                    </div>
                  </div>

                  {/* tagline */}
                  <div className="inline-block text-xs font-semibold text-white px-3 py-1.5 rounded-full mb-4"
                    style={{ background: `linear-gradient(135deg, ${p.accent}cc, #2563EBcc)` }}>
                    {p.tagline}
                  </div>

                  <p className="text-[#475569] text-base leading-relaxed max-w-md">{p.desc}</p>

                  {/* features */}
                  <ul className="space-y-3 mt-6">
                    {p.features.slice(0, 4).map((f) => (
                      <li key={f} className="flex items-start gap-3 text-sm text-[#01193D]">
                        <CheckCircle2 size={16} className="shrink-0 mt-0.5" style={{ color: p.accent }} />
                        {f}
                      </li>
                    ))}
                  </ul>

                  {/* tag pills */}
                  <div className="flex flex-wrap gap-2 mt-5">
                    {p.tags.map(t => (
                      <span key={t} className="text-[10px] font-semibold px-2.5 py-1 rounded-full border"
                        style={{ borderColor: `${p.accent}40`, color: p.accent, background: `${p.accent}10` }}>
                        {t}
                      </span>
                    ))}
                  </div>

                  <Link
                    to={p.path || "/products"}
                    data-testid={`showcase-cta-${p.id}`}
                    className="inline-flex items-center gap-2 font-semibold px-7 py-3.5 rounded-full text-sm mt-8 text-white shadow-lg transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5"
                    style={{ background: `linear-gradient(135deg, ${p.accent}, #2563EB)` }}
                  >
                    Explore {p.shortName || DISPLAY_NAMES[p.id]} <ArrowRight size={15} />
                  </Link>
                </motion.div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
